// Segmented Modeling / Floor plan toggle, shared by both desktop toolbars and
// pinned to the top. The active segment is the highlighted pill; clicking the
// other switches mode (which swaps the whole toolbar, so the pill "moves").

export type AppMode = "modeling" | "floorplan";

let _injected = false;
function injectCSS(): void {
  if (_injected) return;
  _injected = true;
  const style = document.createElement("style");
  style.textContent = `
.dt-modeswitch {
  display: flex; padding: 3px; border-radius: 10px;
  background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1);
}
/* Segments are borderless transparent pills, only the active one filled. The
   !important keeps that intact inside the sidebars, whose broad #dt-ui/#fp-ui
   button rules (ID specificity) would otherwise give each segment a box + border. */
.dt-modeswitch button {
  flex: 1 1 0; min-width: 0; cursor: pointer; border-radius: 7px;
  border: none !important; background: transparent !important;
  color: #aab0c2 !important; padding: 6px 6px !important;
  font: 600 12px system-ui, -apple-system, sans-serif;
  transition: background .12s ease, color .12s ease;
}
.dt-modeswitch button:hover { color: #e7e9f0 !important; }
.dt-modeswitch button.active {
  background: #6fa0ff !important; color: #11141c !important; cursor: default;
}
`;
  document.head.appendChild(style);
}

// Reusable segmented toggle (same pill style as the mode switch). Reactive:
// call setActive() to move the highlight when the underlying state changes.
export function createSegToggle(
  options: { label: string; value: string }[],
  active: string,
  onSelect: (v: string) => void,
): { el: HTMLElement; setActive: (v: string) => void } {
  injectCSS();
  const wrap = document.createElement("div");
  wrap.className = "dt-modeswitch";
  const btns = new Map<string, HTMLButtonElement>();
  let current = active;
  for (const { label, value } of options) {
    const b = document.createElement("button");
    b.textContent = label;
    if (value === active) b.classList.add("active");
    b.addEventListener("click", () => {
      if (value !== current) onSelect(value);
    });
    btns.set(value, b);
    wrap.appendChild(b);
  }
  return {
    el: wrap,
    setActive(v: string): void {
      current = v;
      for (const [val, b] of btns) b.classList.toggle("active", val === v);
    },
  };
}

export function createModeSwitch(
  active: AppMode,
  onSelect: (m: AppMode) => void,
): HTMLElement {
  injectCSS();
  const wrap = document.createElement("div");
  wrap.className = "dt-modeswitch";
  const seg = (label: string, mode: AppMode) => {
    const b = document.createElement("button");
    b.textContent = label;
    if (mode === active) b.classList.add("active");
    b.addEventListener("click", () => {
      if (mode !== active) onSelect(mode);
    });
    wrap.appendChild(b);
  };
  seg("Layout", "floorplan");
  seg("Model", "modeling");
  return wrap;
}
